import { createPortal } from "react-dom";
import { Mic } from "@safers/ui/atoms";

interface RecordingIndicatorProps {
  transcript: string;
  onStop: () => void;
  /** `starting` 은 아직 마이크가 안 열린 구간이다. */
  ready: boolean;
}

/**
 * 음성 입력 중 화면.
 *
 * **`start()` 를 부른 뒤 실제로 마이크가 열리기까지 시간이 걸린다.** 그 사이를 안 보여
 * 주면, 모바일에서 마이크를 눌러 모달이 뜨자마자 말하기 시작해 첫 단어가 통째로
 * 날아간다("김포 현장 보여줘" → "현장 보여줘"). 그래서 준비 구간부터 화면을 띄우고,
 * **말해도 되는 시점을 분명히 알린다.**
 */
export function RecordingIndicator({ transcript, onStop, ready }: RecordingIndicatorProps) {
  return createPortal(
    <div
      className="pointer-events-auto fixed inset-0 z-[1100] flex flex-col items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onStop}
      role="dialog"
      aria-modal="true"
      aria-label={ready ? "음성 인식 중" : "마이크 준비 중"}
    >
      <div
        className="relative flex h-44 w-44 items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        {ready && (
          <>
            <span className="absolute inset-0 animate-ping rounded-full bg-red-500/30" />
            <span className="absolute inset-6 animate-ping rounded-full bg-red-500/20 [animation-delay:0.4s]" />
          </>
        )}
        <div
          className={`relative flex h-28 w-28 items-center justify-center rounded-full text-white shadow-lg ring-8 ring-white/20 transition-colors ${
            ready ? "bg-red-500" : "bg-neutral-500"
          }`}
        >
          <Mic size="xl" />
        </div>
      </div>

      <p
        aria-live="polite"
        className="mt-8 max-w-[80vw] break-keep [overflow-wrap:anywhere] text-center text-xl font-medium text-white drop-shadow"
      >
        {ready ? transcript || "말씀하세요" : "마이크 준비 중…"}
      </p>
      <p className="mt-3 break-keep px-6 text-center text-sm text-white/60">
        {ready ? "말을 멈추면 자동 실행 · 화면 탭/Esc 로 취소" : "잠시 뒤 말씀해 주세요"}
      </p>
    </div>,
    document.body
  );
}
